<template>
  <div class="row">
    <div class="col">
      <div class="card contact-info-form">
        <div class="card-header">
          <h5>{{ __('Contact informations') }}</h5>
        </div>

        <div class="card-body">
          <div class="row">
            <div class="col-sm-6">
              <div class="form-group required">
                <label for="first-name-input">{{ __('First Name') }}</label>
                <input type="text" class="form-control" id="first-name-input" v-model="form.first_name" :class="{'is-invalid': validated && !form.first_name}">
              </div>
            </div>

            <div class="col-sm-6">
              <div class="form-group required">
                <label for="last-name-input">{{ __('Last Name') }}</label>
                <input type="text" class="form-control" id="last-name-input" v-model="form.last_name" :class="{'is-invalid': validated && !form.last_name}">
              </div>
            </div>
          </div>

          <div class="form-group required">
            <label for="address-input">{{ __('Address') }}</label>
            <input type="text" class="form-control" id="address-input" v-model="form.address" :class="{'is-invalid': validated && !form.address}">
          </div>

          <div class="row">
            <div class="col-sm-4">
              <div class="mb-3 form-group required">
                <label for="country-select">{{ __('Country') }}</label>
                <select class="form-control" id="country-select" v-model="form.country_id" :class="{'is-invalid': validated && !form.country_id}">
                  <option v-if="form.country_id === null" value="" disabled></option>
                  <option v-for="item in countries" :value="item.value" :key="`country-item-${item.value}`">{{ item.label }}</option>
                </select>
              </div>
            </div>
            <div class="col-sm-4">
              <div class="form-group required">
                <label for="zip-input">{{ __('Zip') }}</label>
                <input type="text" class="form-control" id="zip-input" v-model="form.zip" :class="{'is-invalid': validated && !form.zip}">
              </div>
            </div>
            <div class="col-sm-4">
              <div class="form-group required">
                <label for="city-input">{{ __('City') }}</label>
                <input type="text" class="form-control" id="city-input" v-model="form.city" :class="{'is-invalid': validated && !form.city}">
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-sm-6">
              <div class="form-group required">
                <label for="email-input">{{ __('Email') }}</label>
                <input type="email" class="form-control" id="email-input" v-model="form.email" :class="{'is-invalid': validated && !form.email}">
              </div>
            </div>
            <div class="col-sm-6">
              <div class="form-group required">
                <label for="phone-input">{{ __('Phone') }}</label>
                <input type="text" class="form-control" id="phone-input" v-model="form.phone" :class="{'is-invalid': validated && !form.phone}">
              </div>
            </div>
          </div>

          <hr>
          <div class="mb-3">
            <div class="row">
              <div class="col-6">
                <h5 class="form-label required mb-2" :class="{'is-invalid': validated && !form.payment_method}">{{ __('Payment method') }}</h5>
                <div class="custom-control custom-radio" v-for="method in paymentMethods" :key="`shipping-method-${method.value}`">
                  <input class="custom-control-input" type="radio" v-model="form.payment_method" :value="method.value" :id="`shipping-method-${method.value}`">
                  <label class="custom-control-label" :for="`shipping-method-${method.value}`">
                    <PayPalLogo v-if="method.value === 'paypal'"/>
                    <TrustPayLogo v-else-if="method.value === 'trustpay'"/>
                    <strong v-else>{{ __(method.name) }}</strong>
                  </label>
                </div>
              </div>

              <div v-show="form.payment_method !== 'cash_on_delivery'" class="col-6">
                <h5 class="form-label required mb-2" :class="{'is-invalid': validated && !form.shipping_method_id}">{{ __('Shipping method') }}</h5>
                <div class="custom-control custom-radio" v-for="option in shippingOptions" :key="`shipping-option-${option.value}`">
                  <input class="custom-control-input" type="radio" v-model="form.shipping_method_id" :value="option.value" :id="`shipping-option-${option.value}`">
                  <label class="custom-control-label" :for="`shipping-option-${option.value}`">
                    <PayPalLogo v-if="option.value === 'paypal'"/>
                    <TrustPayLogo v-else-if="option.value === 'trustpay'"/>
                    <strong v-else>{{ __(option.name) }}</strong>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <hr>

          <div class="my-4">
            <label for="note-textarea">{{ __('Note') }}</label>
            <textarea type="text" class="form-control" id="note-textarea" v-model="form.comment"></textarea>
          </div>

          <hr>

          <p class="terms-and-conditions" v-html="__('Your personal information will be used to process your order, to support your experience on this website and for other purposes described in our privacy policy.')"></p>

          <div v-if="serverErrors.length">
            <hr class="mb-4">
            <ul class="text-danger">
              <li v-for="(error, index) in serverErrors" :key="`server-error-${index}`" class="mb-1">{{ error }}</li>
            </ul>
          </div>
        </div>

        <div class="card-footer d-flex flex-row-reverse">
          <button @click="submit" type="button" class="btn btn-primary order-btn my-1" :disabled="submittingOrder">
            <div v-if="submittingOrder" class="spinner-border text-white" style="width: 24px; height: 24px; border-width: 3px; margin: 0 15px -4px -5px;"></div>
            <span>{{ __('Order') }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Api from "@/Services/Api";
import Validation from "@/mixins/Validation";
import LocalStorage from "@/Services/LocalStorage";
import Order from "@/Services/Order";
import PayPalLogo from "@/components/icons/PayPalLogo";
import TrustPayLogo from "@/components/icons/TrustPayLogo";

export default {
  name: "ContactInformations",
  components: {PayPalLogo, TrustPayLogo},
  emits: ['order-completed'],

  mixins: [Validation],

  data() {
    return {
      submittingOrder: false,
      serverErrors: [],
    };
  },

  watch: {
    '$store.state.form.country_id'() {
      const selected = this.$store.getters.shippingOption;

      this.$store.state.form.payment_method = selected?.defaultPaymentMethod;
    },

    '$store.state.form.payment_method'(value) {
      if (value !== 'cash_on_delivery') {
        const selected = this.$store.getters.shippingOption;

        this.$store.state.form.shipping_method_id = selected?.defaultShippingOption;
      }
    },
  },

  computed: {
    form() {
      return this.$store.state.form;
    },

    countries() {
      return this.$store.getters.countries;
    },

    validated() {
      return this.$store.state.validated;
    },

    paymentMethods() {
      if (!LocalStorage.hasSaved('form')) {
        const defaultShippingOption = this.$store.state.shippingOptions.find(i => i.default);

        if (defaultShippingOption) {
          this.$store.state.form.country_id     = defaultShippingOption.country.value;
          this.$store.state.form.payment_method = defaultShippingOption.defaultPaymentMethod;
        }
      }
      return this.$store.getters.paymentMethods;
    },

    shippingOptions() {
      if (!LocalStorage.hasSaved('form')) {
        const defaultShippingOption = this.$store.state.shippingOptions.find(i => i.default);

        if (defaultShippingOption) {
          this.$store.state.form.country_id      = defaultShippingOption.country.value;
          this.$store.state.form.shipping_method_id = defaultShippingOption.defaultShippingOption;
        }
      }
      return this.$store.getters.shippingOptions;
    },
  },

  methods: {
    submit() {
      if (!this.validate()) return;

      this.submittingOrder = true;
      this.serverErrors    = [];

      const data = {
        locale: this.$store.state.locale,
        ...this.form,
        discount_code: this.$store.state.discount.code,
        products: this.$store.state.products.filter(p => p.quantity > 0),
      };

      Api.order(data)
        .then(data => {
          if (data.errors) {
            this.serverErrors = Object.keys(data.errors).map(key => data.errors[key][0]);
            return;
          }

          if (data.payment === 'paypal') {
            window.location.href = data.links.find(i => i.rel === 'approve').href;
          } else if (data.payment === 'trustpay') {
            window.location.href = data.payment_url;
          } else {
            this.$emit('order-completed');
            Order.clear();

            this.submittingOrder = false;
          }
        })
        .catch(() => this.submittingOrder = false);
    },
  },
}
</script>
